import { toDepositTypeId } from "../../../../helpers/depositType";
import { TInvestmentProductDetails } from "../CreateInvestmentProduct";

/**
 * One saved currency detail, in the shape the form works in.
 *
 * The API answers snake_case and the form is camelCase. Converting once on the
 * way in — rather than per-render in each child, which is what happened before
 * — is what stops the parent array and its children disagreeing about which
 * spelling a field has.
 *
 * `deposit_type` also arrives as an enum NAME (`"SavingsDeposit"`), where
 * everything downstream expects its id.
 */
export function hydrateProductDetail(raw: any): TInvestmentProductDetails {
  return {
    id: raw?.id,
    currencyCode: raw?.currency_code ?? raw?.currencyCode,
    // react-select's shape, so the currency chips render on a saved fund.
    value: raw?.currency_code ?? raw?.currencyCode,
    label: raw?.currency_name ?? raw?.currency_code ?? raw?.currencyCode,
    coreBankingProductId:
      raw?.core_banking_product_id ?? raw?.coreBankingProductId,
    coreBankingTenantId:
      raw?.core_banking_tenant_id ?? raw?.coreBankingTenantId,
    depositType: toDepositTypeId(raw?.deposit_type ?? raw?.depositType),
    externalWalletProvider:
      raw?.external_wallet_provider ?? raw?.externalWalletProvider,
    systemWalletProvider:
      raw?.system_wallet_provider ?? raw?.systemWalletProvider,
    minimumInvestment: raw?.minimum_investment ?? raw?.minimumInvestment,
    maximumInvestment: raw?.maximum_investment ?? raw?.maximumInvestment,
    earlyLiquidationChargePercentage:
      raw?.early_liquidation_charge_percentage ??
      raw?.earlyLiquidationChargePercentage ??
      null,
    investmentInterestPaymentTypeId:
      raw?.investment_interest_payment_type?.id ??
      raw?.investmentInterestPaymentTypeId,
    termsAndConditionId:
      raw?.terms_and_condition_id ?? raw?.termsAndConditionId,
    // Sent back as a boolean and as the string "true" depending on the
    // endpoint. The old screen ran `JSON.parse` on it, which threw when it
    // was absent at all.
    isLocked: toBoolean(raw?.is_locked ?? raw?.isLocked),
    interestRateChart: hydrateChart(
      raw?.interest_rate_chart ?? raw?.interestRateChart,
    ),
  } as TInvestmentProductDetails;
}

function toBoolean(value: unknown): boolean {
  return value === true || value === "true";
}

function hydrateChart(raw: any) {
  if (!raw) {
    return undefined;
  }

  return {
    id: raw?.id,
    fromDate: raw?.from_date ?? raw?.fromDate ?? null,
    toDate: raw?.to_date ?? raw?.toDate ?? null,
    name: raw?.name ?? "",
    description: raw?.description ?? "",
    isPrimaryGroupingByAmount:
      raw?.is_primary_grouping_by_amount ??
      raw?.isPrimaryGroupingByAmount ??
      true,
    chartSlabs: (raw?.chart_slabs ?? raw?.chartSlabs ?? []).map(
      (slab: any) => ({
        id: slab?.id,
        fromAmount: slab?.from_amount ?? slab?.fromAmount ?? null,
        toAmount: slab?.to_amount ?? slab?.toAmount ?? null,
        fromPeriod: slab?.from_period ?? slab?.fromPeriod ?? null,
        toPeriod: slab?.to_period ?? slab?.toPeriod ?? null,
        periodType: slab?.period_type ?? slab?.periodType ?? 0,
        interestRate: slab?.interest_rate ?? slab?.interestRate ?? 0,
        description: slab?.description ?? "",
      }),
    ),
  };
}
